/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

// Modules
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { AppRoutingModule } from './app.routes.module';
// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

// Guards
import { AdminGuard } from './guards/admin.guard';
import { AuthGuard } from './guards/auth.guard';
import { NonAdminGuard } from './guards/nonAdmin.guard';
import { LocalTimeZonePipe } from './pipes/local-time-zone';
// Components
import { DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AppComponent } from './app.component';
import { AdminBlotterComponent } from './components/blotter/admin-blotter/admin-blotter.component';
import { ConfirmDialogComponent } from './components/common/confirm-dialog/confirm.dialog';
import { CustomSnackbarComponent } from './components/common/custom-snackbar/custom-snackbar.component';
import { DynamicFormComponent } from './components/login/dyanmic-forms/dynamic-form.component';
import { LoginFormComponent } from './components/login/login-form/login-form.component';
import { LoginComponent } from './components/login/login.component';
import { SsoErrorComponent } from './components/login/sso-error/sso-error.component';
import { MemberToggleComponent } from './components/party-selector/memberToggleDialog/member-toggle-dialog.component';
import { PartySelectorComponent } from './components/party-selector/party-selector.component';
import { SpinnerModalComponent } from './components/spinner-modal/spinner-modal.component';
import { NavLinksComponent } from './components/top-nav/nav-links/nav-links.component';
import { TopNavComponent } from './components/top-nav/top-nav.component';
import { noAuthDomains } from './constants';
import { createAuthInterceptor } from './interceptors/auth/auth.interceptor';
import { ApplicationConfig } from './models/application-config';
import { sentryHttpInterceptor } from './services/api/sentry-error-handler/sentry-error-handler';
import { AppVersionRefreshService } from './services/app-version-refresh/app-version-refresh.service';
import { FeatureFlagsService } from './services/feature-flags/feature-flags.service';

export const maskConfig: Partial<IConfig> | (() => Partial<IConfig>) = {};

export const createAppModule = (config: ApplicationConfig) => {
  @NgModule({
    declarations: [
      AdminBlotterComponent,
      AppComponent,
      ConfirmDialogComponent,
      CustomSnackbarComponent,
      DynamicFormComponent,
      LocalTimeZonePipe,
      LoginComponent,
      LoginFormComponent,
      MemberToggleComponent,
      NavLinksComponent,
      PartySelectorComponent,
      SpinnerModalComponent,
      SsoErrorComponent,
      TopNavComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
      AngularFirestoreModule,
      AngularFireModule.initializeApp(config.firebase),
      AppRoutingModule,
      BrowserAnimationsModule.withConfig({
        disableAnimations: 'Cypress' in window,
      }),
      BrowserModule,
      FormsModule,
      MatButtonModule,
      MatButtonToggleModule,
      MatCardModule,
      MatDialogModule,
      MatFormFieldModule,
      MatIconModule,
      MatInputModule,
      MatMenuModule,
      NgxMaskModule.forRoot(maskConfig),
      MatProgressSpinnerModule,
      MatSelectModule,
      MatSlideToggleModule,
      MatSnackBarModule,
      MatTabsModule,
      MatTooltipModule,
      ReactiveFormsModule,
    ],
    providers: [
      AdminGuard,
      AuthGuard,
      DatePipe,
      // Guards
      DecimalPipe,
      NonAdminGuard,
      PercentPipe,
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      {
        provide: APP_INITIALIZER,
        useFactory: () => {
          const appVersionRefreshService = inject(AppVersionRefreshService);

          return () => appVersionRefreshService.initializeApplication();
        },
        deps: [AppVersionRefreshService],
        multi: true,
      },
      {
        provide: APP_INITIALIZER,
        useFactory: () => {
          const service = inject(FeatureFlagsService);

          return () => service.fetchFeatureFlags();
        },
        deps: [FeatureFlagsService],
        multi: true,
      },
      provideHttpClient(
        withInterceptors([
          sentryHttpInterceptor,
          createAuthInterceptor(noAuthDomains),
        ]),
      ),
    ],
  })
  class AppModule {}

  return AppModule;
};
